import { useQueryClient } from '@tanstack/react-query';
import { useStoreState } from 'providers/StoreStateContext';
import { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFetchRefreshToken } from 'services/auth';
import { useGetLabel } from 'services/labels';
import { isEmptyId } from 'utils/global/validation.utils';

type ProtectedRouteProps = PropsWithChildren;

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const {
    token,
    refreshToken: currentRefreshToken,
    setToken,
    setRefreshToken,
    tokenData,
    setRedirectPath,
  } = useStoreState();

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { mutateAsync } = useFetchRefreshToken();
  const { t } = useTranslation();
  const label = useGetLabel(!isEmptyId(tokenData?.CurrentAccount));

  const refreshTokens = async (exp: number) => {
    const tokenExp = (exp - 90) * 1000;
    if (tokenExp < Date.now()) {
      console.log('Token expired, refreshing...');
      if (!currentRefreshToken) return;

      await mutateAsync(
        { token: currentRefreshToken },
        {
          onSuccess: data => {
            if (data.token && data.refreshToken) {
              setToken(data.token);
              setRefreshToken(data.refreshToken);
            }
          },
          onError: () => {
            setRedirectPath(pathname);
            navigate(`/login`, { replace: true });
          },
        }
      );
    }
  };

  useEffect(() => {
    if (!tokenData) {
      setRedirectPath(pathname);
      navigate(`/login`, { replace: true });
      queryClient.removeQueries();
    }

    if (tokenData) {
      if (isEmptyId(tokenData.CurrentAccount)) {
        navigate('/label-form');
      }

      const timeout = (tokenData.exp - 60) * 1000 - Date.now();

      if (timeout > 0) {
        console.log(`Token expires in: ${Math.round(timeout / 60000)} min`);
      }

      const timerId = setTimeout(() => refreshTokens(tokenData.exp), timeout);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [currentRefreshToken, token]);

  return <>{children}</>;
}
